<script setup>
import { getCurrentInstance, ref, watch } from 'vue';

const props = defineProps({
    leadId: {
        type: Number,
        required: true,
    },
});

const { proxy } = getCurrentInstance();
const deleteState = ref('');
const deleteDialogOpen = ref(false);
const isContactSynced = ref(false);
const deleteContextState = ref('idle');

const notifyDeleteError = (message = null) => {
    proxy?.$notify({
        type: 'error',
        text: message || _mft('error:error.generalTryAgainLater'),
    });
};

const deleteLead = async () => {
    deleteState.value = 'waiting';

    try {
        const res = await axios.delete(route('bo.api.v1.crm.lead.delete', props.leadId));

        if (res.data?.success !== true) {
            throw new Error(
                res.data?.message
                || res.data?.error
                || (Array.isArray(res.data) ? res.data[1] : null)
                || _mft('crm:lead.delete.cannotBeDeletedSubscription')
            );
        }

        window.location.assign(route('crm.leads'));
    } catch (error) {
        deleteState.value = 'error';
        setTimeout(() => {
            deleteState.value = '';
        }, 3000);

        notifyDeleteError(
            error.response?.data?.message
            || error.response?.data?.error
            || (Array.isArray(error.response?.data) ? error.response.data[1] : null)
            || error.message
        );
    }
};

const loadDeleteContext = async () => {
    deleteContextState.value = 'loading';
    isContactSynced.value = false;

    try {
        const response = await axios.get(route('api.crm.lead.delete-context', props.leadId));
        isContactSynced.value = response.data?.is_contact_synced === true;
        deleteContextState.value = 'loaded';
    } catch (error) {
        console.error('Error loading lead delete context:', error);
        deleteContextState.value = 'error';
    }
};

watch(deleteDialogOpen, (isOpen) => {
    if (isOpen) {
        void loadDeleteContext();
    } else {
        deleteContextState.value = 'idle';
        isContactSynced.value = false;
    }
});
</script>

<template>
    <div>
        <button-base
            :state="deleteState"
            style-type="danger"
            @click="deleteDialogOpen = true"
        >
            {{ _mft('crm:lead.delete.label') }}
        </button-base>

        <modal-basic
            :open="deleteDialogOpen"
            @closed="deleteDialogOpen = false"
        >
            <template #header>
                {{ _mft('crm:lead.delete.label') }}
            </template>
            <template #body>
                <div class="space-y-4">
                    <p class="text-sm text-gray-700">
                        {{ _mft('crm:lead.delete.cannotBeUndone') }}
                    </p>

                    <p
                        v-if="deleteContextState === 'loaded' && isContactSynced"
                        class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3"
                    >
                        {{ _mft('crm:lead.delete.rememberDeleteSynced') }}
                    </p>

                    <p
                        v-else-if="deleteContextState === 'error'"
                        class="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3"
                    >
                        {{ _mft('error:error.generalTryAgainLater') }}
                    </p>

                    <div
                        v-else-if="deleteContextState === 'loading'"
                        class="flex items-center gap-2 text-sm text-gray-500"
                    >
                        <mf-spinner-small class="w-4 h-4" />
                        <span>{{ _mft('crm:lead.delete.loadingSyncStatus') }}</span>
                    </div>

                    <div class="flex justify-end space-x-2">
                        <button-secondary @click="deleteDialogOpen = false">
                            {{ _mft('shared:action.cancel') }}
                        </button-secondary>
                        <button-base
                            style-type="danger"
                            :disabled="deleteContextState === 'loading'"
                            @click="deleteLead"
                        >
                            {{ _mft('crm:lead.delete.confirm') }}
                        </button-base>
                    </div>
                </div>
            </template>
        </modal-basic>
    </div>
</template>
